import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import {
  getCities,
  getIndustryData,
  getJobTypeData,
  getJobTitles,
  getExperience,
} from "../../../service/api";
import "bootstrap/dist/css/bootstrap.min.css";

const JobSearchBar = ({
  initialTitle = "",
  initialCity = "",
  initialIndustry = "",
  initialJobType = "",
  initialdExperienceRange = "",
  initialMinSalary = "",
  initialMaxSalary = "",
  btnLoading = false,
  onSearch,
  customBasePath = "",
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ---------------- STATES ----------------
  const [jobTitle, setJobTitle] = useState(initialTitle);
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [selectedIndustry, setSelectedIndustry] = useState(initialIndustry);
  const [selectedJobType, setSelectedJobType] = useState(initialJobType);
  const [selectedExperienceRange, setSelectedExperienceRange] = useState(
    initialdExperienceRange,
  );

  const [minSalary, setMinSalary] = useState(initialMinSalary);
  const [maxSalary, setMaxSalary] = useState(initialMaxSalary);
  const suggestionRef = useRef(null);

  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [experienceRange, setExperienceRange] = useState([]);

  const [loadingCities, setLoadingCities] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ---------------- LOAD SUGGESTIONS ----------------
  const loadSuggestions = async (text) => {
    if (!text) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const res = await getJobTitles(text);
      if (res.status === 200 && res.data?.data) {
        setSuggestions(res.data.data);
        setShowSuggestions(true);
      }
    } catch (e) {
      console.error("Suggestion error:", e);
    }
  };

  // ---------------- LOAD DROPDOWN DATA ----------------
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoadingCities(true);

        const cityRes = await getCities();
        if (cityRes.status === 200 && cityRes.data?.data) {
          setCities(cityRes.data.data.map((c) => ({ value: c, label: c })));
        }

        const industryRes = await getIndustryData();
        if (industryRes.data?.data) {
          setIndustries(
            industryRes.data.data.map((i) => ({
              value: i.name,
              label: i.name,
            })),
          );
        }

        const expRes = await getExperience();
        if (expRes.data?.data) {
          setExperienceRange(
            expRes.data.data.map((i) => ({
              value: i.name,
              label: i.name,
            })),
          );
        }

        const jobRes = await getJobTypeData();
        if (jobRes.data?.data?.jobTypesList) {
          setJobTypes(
            jobRes.data.data.jobTypesList.map((j) => ({
              value: j.name,
              label: j.name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching filters:", error);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchFilters();
  }, []);

  // ---------------- SYNC WITH URL ----------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    setJobTitle(params.get("title") || "");
    setSelectedCity(params.get("city") || "");
    setSelectedIndustry(params.get("industryType") || "");
    setSelectedJobType(params.get("jobType") || "");
    setSelectedExperienceRange(params.get("experience") || "");
    setMinSalary(params.get("minSalary") || "");
    setMaxSalary(params.get("maxSalary") || "");
  }, [location.search]);

  // ---------------- SEARCH HANDLER ----------------
  const handleSearch = () => {
    const filters = {
      title: jobTitle,
      city: selectedCity,
      industryType: selectedIndustry,
      jobType: selectedJobType,
      experience: selectedExperienceRange,
      minSalary,
      maxSalary,
    };

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const basePath = customBasePath || location.pathname;
    navigate(`${basePath}?${params.toString()}`, { replace: false });

    if (onSearch) onSearch(filters);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---------------- RESET HANDLER ----------------
  const handleReset = () => {
    setJobTitle("");
    setSelectedCity("");
    setSelectedIndustry("");
    setSelectedJobType("");
    setSelectedExperienceRange("");
    setMinSalary("");
    setMaxSalary("");

    navigate(location.pathname, { replace: false });

    if (onSearch)
      onSearch({
        title: "",
        city: "",
        industryType: "",
        jobType: "",
        experience: "",
        minSalary: "",
        maxSalary: "",
      });
  };

  return (
    <>
      <div className="side-job-search">
        <div className="searchBar innersearch">
          <div className="row-group-search">
            {/* ---------------- JOB TITLE INPUT ---------------- */}
            <div className="form-group form-group-1 hj-form">
              <div style={{ position: "relative" }} ref={suggestionRef}>
                <i className="fa-solid fa-magnifying-glass"></i>

                <input
                  type="text"
                  className="input-filter-search"
                  placeholder="Job title, keywords..."
                  value={jobTitle}
                  onChange={(e) => {
                    setJobTitle(e.target.value);
                    loadSuggestions(e.target.value);
                  }}
                />

                {/* SUGGESTION BOX */}
                {showSuggestions && suggestions.length > 0 && (
                  <div
                    className="suggestion-box"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ddd",
                      zIndex: 1000,
                      borderRadius: "6px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {suggestions.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setJobTitle(item);
                          setShowSuggestions(false);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ---------------- CITY ---------------- */}
            <div className="form-group form-group-2">
              <i className="fa-solid fa-location-crosshairs"></i>
              <Select
                options={cities}
                isLoading={loadingCities}
                placeholder="Search Location..."
                value={cities.find((c) => c.value === selectedCity) || null}
                onChange={(opt) => setSelectedCity(opt ? opt.value : "")}
              />
            </div>

            {/* ---------------- INDUSTRY ---------------- */}
            <div className="form-group form-group-2">
              <i className="fa-solid fa-briefcase"></i>
              <Select
                options={industries}
                placeholder="Select Industry"
                value={
                  selectedIndustry
                    ? { value: selectedIndustry, label: selectedIndustry }
                    : null
                }
                onChange={(opt) => setSelectedIndustry(opt ? opt.value : "")}
              />
            </div>

            {/* ---------------- JOB TYPE ---------------- */}
            <div className="form-group form-group-2">
              <i className="fa-solid fa-user-tie"></i>
              <Select
                options={jobTypes}
                placeholder="Select Job Type"
                value={
                  selectedJobType
                    ? { value: selectedJobType, label: selectedJobType }
                    : null
                }
                onChange={(opt) => setSelectedJobType(opt ? opt.value : "")}
              />
            </div>

            {/* ---------------- EXPERIENCE ---------------- */}
            <div className="form-group form-group-2">
              <i className="fa-solid fa-layer-group"></i>
              <Select
                options={experienceRange}
                placeholder="Select Experience"
                value={
                  selectedExperienceRange
                    ? {
                        value: selectedExperienceRange,
                        label: selectedExperienceRange,
                      }
                    : null
                }
                onChange={(opt) =>
                  setSelectedExperienceRange(opt ? opt.value : "")
                }
              />
            </div>

            {/* ---------------- SALARY RANGE SLIDER ---------------- */}
            <label className="salary-label">Salary Range</label>

            <div className="salary-wrapper">
              <div
                className="salary-range-track"
                style={{
                  "--min": (minSalary / 20000) * 100,
                  "--max": (maxSalary / 200000) * 100,
                }}
              ></div>

              <input
                type="range"
                min="0"
                max="20000"
                value={minSalary}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val <= maxSalary) setMinSalary(val);
                }}
              />

              <input
                type="range"
                min="0"
                max="200000"
                value={maxSalary}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= minSalary) setMaxSalary(val);
                }}
              />

              <div className="salary-values">
                <span>₹{minSalary}</span>
                <span>₹{maxSalary}</span>
              </div>
            </div>

            {/* ---------------- BUTTONS ---------------- */}
            <div className="d-flex form-group-4">
              <button
                type="button"
                className="searchbtn"
                disabled={btnLoading}
                onClick={handleSearch}
              >
                {btnLoading ? "Searching..." : "Search"}
              </button>

              <button
                type="button"
                className="searchbtn"
                style={{
                  backgroundColor: "#270b79",
                  border: "1px solid #270b79",
                  borderRadius: "10px",
                  marginLeft: "4px",
                }}
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobSearchBar;
